import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { creditsService, type CreditBalance } from '../services/credits';
import { TOPUP_PACKS } from '@traaker/shared';

/** Smallest pack that covers a shortfall (needed − available); largest as fallback. */
function recommendPack(shortfall: number): string {
  const sorted = [...TOPUP_PACKS].sort((a, b) => a.credits - b.credits);
  return (sorted.find((p) => p.credits >= shortfall) ?? sorted[sorted.length - 1]).key;
}

interface CreditState {
  /** Agency credit balance; null for non-agency tenants or before first load. */
  balance: CreditBalance | null;
  isAgency: boolean;
  refresh: () => Promise<void>;
  /** Top-up modal control — shared by the chip + the INSUFFICIENT_CREDITS handler. */
  topupOpen: boolean;
  openTopup: (recommendedPackKey?: string) => void;
  closeTopup: () => void;
  recommendedPackKey?: string;
}

const CreditContext = createContext<CreditState>({
  balance: null,
  isAgency: false,
  refresh: async () => {},
  topupOpen: false,
  openTopup: () => {},
  closeTopup: () => {},
});

/** Poll every 60s while visible — balance isn't second-critical (the gate is server-side). */
const POLL_INTERVAL_MS = 60_000;

export function CreditProvider({ children }: { children: ReactNode }) {
  const { user, tenant } = useAuth();
  const isAgency = tenant?.tier === 'agency';
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const [recommendedPackKey, setRecommendedPackKey] = useState<string | undefined>();
  const pollRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!user || !isAgency) return;
    try {
      setBalance(await creditsService.getBalance());
    } catch {
      // Silent — chip is non-critical; keep prior state.
    }
  }, [user, isAgency]);

  const openTopup = useCallback((recommendedPackKey?: string) => {
    setRecommendedPackKey(recommendedPackKey);
    setTopupOpen(true);
  }, []);
  const closeTopup = useCallback(() => setTopupOpen(false), []);

  useEffect(() => {
    if (!user || !isAgency) {
      setBalance(null);
      return;
    }
    refresh();
    pollRef.current = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [user, isAgency, refresh]);

  // INSUFFICIENT_CREDITS (broadcast by api.ts) → open the top-up modal with a
  // recommended pack sized to the shortfall.
  useEffect(() => {
    if (!isAgency) return;
    const onShortfall = (e: Event) => {
      const { needed, available } = (e as CustomEvent).detail || {};
      const shortfall = Math.max(1, (needed ?? 0) - (available ?? 0));
      openTopup(recommendPack(shortfall));
    };
    window.addEventListener('traaker:insufficient-credits', onShortfall);
    return () => window.removeEventListener('traaker:insufficient-credits', onShortfall);
  }, [isAgency, openTopup]);

  return (
    <CreditContext.Provider
      value={{ balance, isAgency, refresh, topupOpen, openTopup, closeTopup, recommendedPackKey }}
    >
      {children}
    </CreditContext.Provider>
  );
}

export function useCredits() {
  return useContext(CreditContext);
}
